import { formatScoringSummaryEvent } from "../../../common/formatScoringSummaryEvent.football.ts";
import type { TeamNum } from "../../../common/types.ts";
import { PlayByPlayLoggerBase } from "../GameSim/PlayByPlayLoggerBase.ts";

export type PlayByPlayEventInputScore =
	| {
			type: "kickoffReturn";
			automaticFirstDown?: boolean;
			clock: number;
			names: string[];
			t: TeamNum;
			td: boolean;
			yds: number;
			totalKrTD: number | undefined;
	  }
	| {
			type: "puntReturn";
			clock: number;
			names: string[];
			t: TeamNum;
			td: boolean;
			yds: number;
			totalPrTD: number | undefined;
	  }
	| {
			type: "fumbleRecovery";
			clock: number;
			lost: boolean;
			names: string[];
			safety: boolean;
			t: TeamNum;
			td: boolean;
			touchback: boolean;
			twoPointConversionTeam: TeamNum | undefined;
			yds: number;
			ydsBefore: number;
	  }
	| {
			type: "interception";
			clock: number;
			names: string[];
			totalPssInt: number | undefined;
			totalDefInt: number | undefined;
			t: TeamNum;
			twoPointConversionTeam: TeamNum | undefined;
			yds: number;
	  }
	| {
			type: "interceptionReturn";
			clock: number;
			names: string[];
			t: TeamNum;
			td: boolean;
			touchback: boolean;
			twoPointConversionTeam: TeamNum | undefined;
			yds: number;
	  }
	| {
			type: "passComplete";
			clock: number;
			names: string[];
			totalPssTD: number | undefined;
			totalRecTD: number | undefined;
			safety: boolean;
			t: TeamNum;
			td: boolean;
			twoPointConversionTeam: TeamNum | undefined;
			yds: number;
	  }
	| {
			type: "run";
			clock: number;
			names: string[];
			totalRusTD: number | undefined;
			safety: boolean;
			t: TeamNum;
			td: boolean;
			twoPointConversionTeam: TeamNum | undefined;
			yds: number;
	  }
	| {
			type: "onsideKickRecovery";
			clock: number;
			names: string[];
			success: boolean;
			t: TeamNum;
			td: boolean;
	  }
	| {
			type: "extraPointAttempt" | "fieldGoalAttempt";
			clock: number;
			names: string[];
			t: TeamNum;
			yds: number;
	  }
	| {
			type: "extraPoint" | "fieldGoal";
			clock: number;
			made: boolean;
			names: string[];
			t: TeamNum;
			yds: number;
	  }
	| {
			type: "sack";
			clock: number;
			names: string[];
			totalDefSk: number | undefined;
			safety: boolean;
			t: TeamNum;
			yds: number;
	  }
	| {
			type: "twoPointConversionFailed";
			clock: number;
			t: TeamNum;
	  }
	| {
			type: "shootoutShot";
			t: TeamNum;
			names: string[];
			made: boolean;
			att: number;
			yds: number;
			clock: number;
	  };

export type PlayByPlayEventInput =
	| PlayByPlayEventInputScore
	| {
			type: "quarter";
			clock: number;
			quarter: number;
			startsWithKickoff: boolean;
	  }
	| {
			type: "overtime";
			clock: number;
			overtimes: number;
			startsWithKickoff: boolean;
	  }
	| {
			type: "gameOver";
			clock: number;
	  }
	| {
			type: "injury";
			clock: number;
			injuredPID: number;
			names: string[];
			t: TeamNum;
	  }
	| {
			type: "kickoff";
			clock: number;
			names: string[];
			t: TeamNum;
			touchback: boolean;
			yds: number;
	  }
	| {
			type: "punt";
			clock: number;
			names: string[];
			t: TeamNum;
			touchback: boolean;
			yds: number;
	  }
	| {
			type: "fumble";
			clock: number;
			names: string[];
			totalFmb: number | undefined;
			totalDefFmbFrc: number | undefined;
			t: TeamNum;
	  }
	| {
			type: "dropback";
			clock: number;
			names: string[];
			t: TeamNum;
	  }
	| {
			type: "passIncomplete";
			clock: number;
			names: string[];
			t: TeamNum;
			yds: number;
	  }
	| {
			type: "handoff";
			clock: number;
			names: string[];
			t: TeamNum;
	  }
	| {
			type: "onsideKick";
			clock: number;
			names: string[];
			t: TeamNum;
	  }
	| {
			type: "penalty";
			automaticFirstDown: boolean;
			clock: number;
			decision: "accept" | "decline";
			halfDistanceToGoal: boolean;
			names: string[];
			offsetStatus: "offset" | "overrule" | undefined;
			penaltyName: string;
			placeOnOne: boolean;
			possessionAfter: TeamNum;
			scrimmageAfter: number;
			spotFoul: boolean;
			t: TeamNum;
			tackOn: boolean;
			yds: number;
	  }
	| {
			type: "penaltyCount";
			clock: number;
			count: number;
			offsetStatus: "offset" | "overrule" | undefined;
	  }
	| {
			type: "timeout";
			clock: number;
			offense: boolean;
			numLeft: number;
			t: TeamNum;
			toStopClock: boolean;
	  }
	| {
			type: "twoMinuteWarning";
			clock: number;
	  }
	| {
			type: "kneel";
			clock: number;
			names: string[];
			t: TeamNum;
			yds: number;
	  }
	| {
			type: "flag";
			clock: number;
	  }
	| {
			type: "twoPointConversion";
			clock: number;
			t: TeamNum;
	  }
	| {
			type: "goingForItOn4th";
			clock: number;
			t: TeamNum;
	  }
	| {
			type: "puntTeam";
			clock: number;
			t: TeamNum;
	  }
	| {
			type: "turnoverOnDowns";
			clock: number;
			t: TeamNum;
	  }
	| {
			type: "shootoutStart";
			rounds: number;
			clock: number;
	  }
	| {
			type: "shootoutTie";
			clock: number;
	  }
	| {
			type: "timeouts";
			timeouts: [number, number];
	  };

export type PlayByPlayEventOutput =
	| PlayByPlayEventInput
	| {
			type: "removeLastScore";
	  }
	| {
			type: "clock";
			awaitingKickoff: TeamNum | undefined;
			awaitingAfterTouchdown: boolean;
			clock: number;
			down: number;
			scrimmage: number;
			t: TeamNum;
			toGo: number;
	  };

export type PlayByPlayEventScore = PlayByPlayEventInputScore & {
	quarter: number;
};

class FootballPlayByPlayLogger extends PlayByPlayLoggerBase<PlayByPlayEventOutput> {
	scoringSummary: (
		| PlayByPlayEventScore
		| {
				type: "removeLastScore";
		  }
	)[] = [];

	private quarter = 1;

	constructor(active: boolean) {
		super(active);
		this.active = active;
	}

	logEvent(event: PlayByPlayEventInput) {
		if (event.type === "quarter" || event.type === "overtime") {
			this.quarter += 1;
		}

		if (this.active) {
			this.playByPlay.push({
				...event,
			});
		}

		const scoringSummaryEvent = formatScoringSummaryEvent(event, this.quarter);
		if (scoringSummaryEvent) {
			this.scoringSummary.push(scoringSummaryEvent);
		}
	}

	logClock({
		awaitingKickoff,
		awaitingAfterTouchdown,
		clock,
		down,
		scrimmage,
		t,
		toGo,
	}: {
		awaitingKickoff: TeamNum | undefined;
		awaitingAfterTouchdown: boolean;
		clock: number;
		down: number;
		scrimmage: number;
		t: TeamNum;
		toGo: number;
	}) {
		if (!this.active) {
			return;
		}

		this.playByPlay.push({
			type: "clock",
			awaitingKickoff,
			awaitingAfterTouchdown,
			clock,
			down,
			scrimmage,
			t,
			toGo,
		});
	}

	removeLastScore() {
		if (this.active) {
			this.playByPlay.push({
				type: "removeLastScore",
			});
		}

		this.scoringSummary.push({
			type: "removeLastScore",
		});
	}
}

export default FootballPlayByPlayLogger;
